import { createPortal } from 'react-dom';
import { RiCloseLine, RiLoader4Line, RiErrorWarningLine, RiInformationLine, RiRefreshLine } from '@remixicon/react';
import { formatMemberStatus } from '../utils';

/**
 * InviteDecisionModal
 *
 * Follow-up modal fired when the Admin's "Add Member" submit is
 * intercepted by the pre-flight `findUserByEmail` check. The email
 * already belongs to a user in some state; this modal picks the right
 * copy + action for that state so we never hit the archived-then-
 * reinvite INSERT collision.
 *
 * Decision `kind`:
 *   - `restore`             — ARCHIVED same-org         (action: Restore)
 *   - `reactivate`          — INACTIVE same-org         (action: Reactivate)
 *   - `activate`            — ONBOARDING_COMPLETED same-org (action: Activate)
 *   - `already_member`      — ACTIVE same-org            (info only)
 *   - `crossorg`            — any status, different org  (info only, contact support)
 *   - `onboarding_started`  — ONBOARDING_STARTED same-org (info only, wait for completion)
 *   - `suspended`           — SUSPENDED any org          (info only, contact support)
 *
 * Action modals call `onConfirm(decision)` which PATCHes the row via
 * updateUser. Info modals just close.
 */
const InviteDecisionModal = ({
  open,
  decision,
  saving = false,
  onClose,
  onConfirm,
}) => {
  if (!open || !decision) return null;

  const { kind, existingMember, payload } = decision;
  const memberName = existingMember
    ? ([existingMember.first_name, existingMember.last_name].filter(Boolean).join(' ') || existingMember.email)
    : '';
  const memberRole = existingMember?.role || '';
  const memberStatusLabel = existingMember ? formatMemberStatus(existingMember.status).label : '';

  const ACTIONABLE_KINDS = new Set(['restore', 'reactivate', 'activate']);
  const isActionable = ACTIONABLE_KINDS.has(kind);

  const copy = getCopy(kind, { memberName, memberRole, memberStatusLabel, invitedRole: payload?.role });

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => !saving && onClose?.()}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className={`text-lg font-semibold ${copy.titleClass}`}>{copy.title}</h2>
          <button
            type="button"
            onClick={() => !saving && onClose?.()}
            disabled={saving}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${copy.iconBg}`}>
              {saving ? (
                <RiLoader4Line className={`w-5 h-5 animate-spin ${copy.iconColor}`} />
              ) : (
                <copy.Icon className={`w-5 h-5 ${copy.iconColor}`} />
              )}
            </div>
            <div className="text-sm text-gray-700 leading-relaxed">
              {copy.body}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => onClose?.()}
              disabled={saving}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isActionable ? 'Cancel' : 'Close'}
            </button>
            {isActionable && (
              <button
                type="button"
                onClick={() => onConfirm?.(decision)}
                disabled={saving}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed ${copy.confirmBg}`}
              >
                {saving && <RiLoader4Line className="w-4 h-4 animate-spin" />}
                {saving ? copy.confirmSaving : copy.confirmLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Copy + styling for each decision kind. Keeps the render body clean.
function getCopy(kind, { memberName, memberRole, memberStatusLabel, invitedRole }) {
  switch (kind) {
    case 'restore':
      return {
        title: 'Restore member',
        titleClass: 'text-gray-900',
        Icon: RiRefreshLine,
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-600',
        confirmLabel: 'Restore',
        confirmSaving: 'Restoring…',
        confirmBg: 'bg-primary-700 hover:bg-primary-800',
        body: (
          <>
            <p>
              <span className="font-semibold">{memberName}</span> already exists as an{' '}
              <span className="font-semibold">archived</span> member of your organization
              {memberRole ? <> (previous role: <span className="font-semibold">{memberRole}</span>)</> : null}.
            </p>
            <p className="mt-2">
              Restoring reactivates their account
              {invitedRole ? <> with the role <span className="font-semibold">{invitedRole}</span></> : null}
              . <span className="font-medium">Note:</span> quotes and clients that transferred to the owner on archive stay with the owner — restoration does not reverse that transfer.
            </p>
          </>
        ),
      };
    case 'reactivate':
      return {
        title: 'Reactivate member',
        titleClass: 'text-gray-900',
        Icon: RiRefreshLine,
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-600',
        confirmLabel: 'Reactivate',
        confirmSaving: 'Reactivating…',
        confirmBg: 'bg-primary-700 hover:bg-primary-800',
        body: (
          <>
            <p>
              <span className="font-semibold">{memberName}</span> is currently deactivated in your organization.
              Reactivate them
              {invitedRole ? <> with the role <span className="font-semibold">{invitedRole}</span></> : null}?
            </p>
            <p className="mt-2 text-gray-500">No invitation email will be sent — they can sign in immediately.</p>
          </>
        ),
      };
    case 'activate':
      return {
        title: 'Activate member',
        titleClass: 'text-gray-900',
        Icon: RiRefreshLine,
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-600',
        confirmLabel: 'Activate',
        confirmSaving: 'Activating…',
        confirmBg: 'bg-primary-700 hover:bg-primary-800',
        body: (
          <>
            <p>
              <span className="font-semibold">{memberName}</span> has completed onboarding for your organization but hasn't been activated yet.
            </p>
            <p className="mt-2">
              Activate them
              {invitedRole ? <> as <span className="font-semibold">{invitedRole}</span></> : null}? No new invite email is needed.
            </p>
          </>
        ),
      };
    case 'already_member':
      return {
        title: 'Already a member',
        titleClass: 'text-gray-900',
        Icon: RiInformationLine,
        iconBg: 'bg-blue-50',
        iconColor: 'text-blue-600',
        confirmLabel: '',
        confirmSaving: '',
        confirmBg: '',
        body: (
          <>
            <p>
              <span className="font-semibold">{memberName}</span> is already an active member of your organization.
            </p>
            <p className="mt-2 text-gray-500">Edit their profile from the Team Members panel to change their role.</p>
          </>
        ),
      };
    case 'crossorg':
      return {
        title: "Can't invite this user",
        titleClass: 'text-red-600',
        Icon: RiErrorWarningLine,
        iconBg: 'bg-red-50',
        iconColor: 'text-red-600',
        confirmLabel: '',
        confirmSaving: '',
        confirmBg: '',
        body: (
          <>
            <p>This email belongs to a user in a different organization.</p>
          </>
        ),
      };
    case 'onboarding_started':
      return {
        title: 'Onboarding in progress',
        titleClass: 'text-gray-900',
        Icon: RiInformationLine,
        iconBg: 'bg-blue-50',
        iconColor: 'text-blue-600',
        confirmLabel: '',
        confirmSaving: '',
        confirmBg: '',
        body: (
          <>
            <p>
              <span className="font-semibold">{memberName}</span> has already started onboarding for your organization.
            </p>
            <p className="mt-2 text-gray-500">
              Wait for them to finish, then activate them from the Team Members panel. No new invite will be sent.
            </p>
          </>
        ),
      };
    case 'suspended':
      return {
        title: 'Account suspended',
        titleClass: 'text-red-600',
        Icon: RiErrorWarningLine,
        iconBg: 'bg-red-50',
        iconColor: 'text-red-600',
        confirmLabel: '',
        confirmSaving: '',
        confirmBg: '',
        body: (
          <>
            <p>This account is suspended and can't accept invitations.</p>
            <p className="mt-2 text-gray-500">Contact support to review the suspension.</p>
          </>
        ),
      };
    default:
      return {
        title: 'Notice',
        titleClass: 'text-gray-900',
        Icon: RiInformationLine,
        iconBg: 'bg-gray-100',
        iconColor: 'text-gray-500',
        confirmLabel: '',
        confirmSaving: '',
        confirmBg: '',
        body: <p>This email is already registered elsewhere.</p>,
      };
  }
}

export default InviteDecisionModal;
